import React, { useMemo, useState } from 'react';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import InputLabel from '@mui/material/InputLabel';
import Paper from '@mui/material/Paper';
import AddIcon from '@mui/icons-material/Add';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Modal from '@/common/components/Modal/index';
import cx from 'classnames';
import { Notification } from '@/common/utils/index';
import CreateResellerModal from '@/modules/admin-reseller-form/CreateResellerModal';
import styles from '@/modules/admin-access-codes/styles.less';
import modalStyles from '@/common/styles/modal-form-primary.less';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useGenerateAccessCodesMutation } from '@/entities/admin-access-codes';

const MIN_CODES = 1;
const MAX_CODES = 1000;

const GenerateAccessCode = ({ resellerOptions = [] }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [createResellerOpen, setCreateResellerOpen] = useState(false);

  const validation = useMemo(
    () =>
      yup.object({
        count: yup
          .number()
          .typeError(t('Enter a number from 1 to 1000'))
          .integer(t('Enter a number from 1 to 1000'))
          .min(MIN_CODES, t('Enter a number from 1 to 1000'))
          .max(MAX_CODES, t('Enter a number from 1 to 1000'))
          .required(t('Enter a number from 1 to 1000')),
        reseller: yup.object().nullable().required(t('Field is required')),
      }),
    [t]
  );

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { isValid },
  } = useForm({
    mode: 'onChange',
    resolver: yupResolver(validation),
    defaultValues: {
      count: '1',
      reseller: null,
    },
  });

  const count = watch('count');
  const reseller = watch('reseller');

  const closeModal = () => {
    setOpen(false);
    reset({
      count: '1',
      reseller: null,
    });
  };

  const generateMutation = useGenerateAccessCodesMutation({
    onSuccess: (response) => {
      const createdCount = response?.data?.length ?? Number(count);

      Notification.success(
        t('Generated {{count}} access codes', { count: createdCount })
      );
      closeModal();
    },
    onError: () => {
      Notification.error(t('Failed to generate access codes'));
    },
  });

  const handleGenerate = handleSubmit((form) => {
    generateMutation.mutate({
      count: Number(form.count),
      resellerId: form.reseller.id,
    });
  });

  const handleCreateReseller = () => setCreateResellerOpen(true);

  return (
    <>
      <Button
        className={styles.pageHeaderBtn}
        variant="contained"
        color="success"
        type="button"
        onClick={() => setOpen(true)}
      >
        <AddIcon />
        {t('Generate Access Code')}
      </Button>
      <Modal
        className={cx(modalStyles.modal, styles.generateModal)}
        open={open}
        onClose={generateMutation.isPending ? undefined : closeModal}
        overlay
        overlayOnClose={!generateMutation.isPending}
      >
        <CreateResellerModal
          open={createResellerOpen}
          onClose={() => setCreateResellerOpen(false)}
          onCreated={(createdReseller) => {
            if (!createdReseller) return;

            setValue('reseller', createdReseller, {
              shouldDirty: true,
              shouldValidate: true,
            });
            setCreateResellerOpen(false);
          }}
        />
        <div className={modalStyles.modalHeader}>
          <h3>{t('Generate Access Code')}</h3>
        </div>
        <div className={cx(modalStyles.modalForm, styles.generateModalForm)}>
          <div className={cx(modalStyles.modalBody, styles.generateModalBody)}>
            <div className={styles.generateModalContent}>
              <p className={styles.generateModalDescription}>
                {t(
                  'Generate new free-registration codes for funeral homes to distribute to users.'
                )}
              </p>
              <div className={styles.generateModalField}>
                <Controller
                  name="count"
                  control={control}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      id="generate-access-code-count"
                      fullWidth
                      label={t('Number of codes')}
                      required
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message || ''}
                      onChange={(event) =>
                        field.onChange(event.target.value.replace(/\D/g, ''))
                      }
                      inputProps={{
                        inputMode: 'numeric',
                        min: MIN_CODES,
                        max: MAX_CODES,
                      }}
                    />
                  )}
                />
              </div>
              <div className={styles.generateModalField}>
                <Controller
                  name="reseller"
                  control={control}
                  render={({ field, fieldState }) => (
                    <Autocomplete
                      options={resellerOptions}
                      value={field.value}
                      onChange={(_, value) => field.onChange(value)}
                      getOptionLabel={(option) => option?.name || ''}
                      isOptionEqualToValue={(option, value) =>
                        option.id === value.id
                      }
                      PaperComponent={(props) => (
                        <Paper {...props}>
                          {props.children}
                          <Button
                            type="button"
                            variant="contained"
                            color="success"
                            fullWidth
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={handleCreateReseller}
                            sx={{ height: '48px' }}
                            startIcon={<AddIcon />}
                          >
                            {t('Create New Reseller')}
                          </Button>
                        </Paper>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          required
                          label={t('Reseller')}
                          id="generate-access-code-reseller"
                          error={!!fieldState.error}
                          helperText={fieldState.error?.message || ''}
                        />
                      )}
                    />
                  )}
                />
              </div>
            </div>
          </div>
          <div className={styles.generateModalFooter}>
            <Button
              variant="outlined"
              color="secondary"
              onClick={closeModal}
              disabled={generateMutation.isPending}
              size="large"
            >
              {t('Cancel')}
            </Button>
            <Button
              variant="contained"
              color="success"
              onClick={handleGenerate}
              disabled={!isValid || !reseller?.id || generateMutation.isPending}
              loading={generateMutation.isPending}
              size="large"
            >
              {t('Generate Code')}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default GenerateAccessCode;
